import { Component } from '@theme/component';

const FIELD_CONFIG = {
  waist: {
    inputRef: 'waistInput',
    errorRef: 'waistError',
    requiredMessage: 'waistRequired',
    invalidMessage: 'waistInvalid',
  },
  length: {
    inputRef: 'lengthInput',
    errorRef: 'lengthError',
    requiredMessage: 'lengthRequired',
    invalidMessage: 'lengthInvalid',
  },
  type: {
    inputRef: 'typeSelect',
    errorRef: 'typeError',
    requiredMessage: 'typeRequired',
  },
};

class BiasCalculator extends Component {
  requiredRefs = [
    'form',
    'waistInput',
    'lengthInput',
    'typeSelect',
    'waistError',
    'lengthError',
    'typeError',
    'results',
    'roundedValue',
    'liveRegion',
    'messages',
  ];

  #hasCalculatedOnce = false;
  #messages = {};
  #numberFormatter = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  connectedCallback() {
    super.connectedCallback();
    this.#messages = this.#readMessages();
  }

  handleSubmit(event) {
    event.preventDefault();
    this.#calculateAndRender({ focusFirstError: true });
  }

  handleFieldInteraction() {
    if (!this.#hasCalculatedOnce) return;

    this.#calculateAndRender({ focusFirstError: false });
  }

  handleResetClick(event) {
    event.preventDefault();
    this.resetCalculator();
  }

  getSkirtCoefficient(type) {
    switch (type) {
      case 'full':
        return 1;
      case 'three-quarter':
        return 0.75;
      case 'half':
        return 0.5;
      default:
        return null;
    }
  }

  validateInputs(values) {
    const errors = {};
    const parsedValues = {
      waistCm: null,
      lengthCm: null,
      skirtType: values.skirtType,
    };

    parsedValues.waistCm = this.#validateNumberField({
      rawValue: values.waistCm,
      fieldName: 'waist',
      errors,
    });

    parsedValues.lengthCm = this.#validateNumberField({
      rawValue: values.lengthCm,
      fieldName: 'length',
      errors,
    });

    if (!values.skirtType) {
      errors.type = this.#messages.errors?.typeRequired ?? '';
    }

    return {
      errors,
      values: parsedValues,
      isValid: Object.keys(errors).length === 0,
    };
  }

  calculateHemCm(waistCm, lengthCm, coefficient) {
    return waistCm + 2 * Math.PI * lengthCm * coefficient;
  }

  addAllowanceCm(hemCm) {
    return hemCm + 15;
  }

  roundUpToNearestTenCm(valueCm) {
    return Math.ceil(valueCm / 10) * 10;
  }

  formatMetersFr(valueCm) {
    return `${this.#numberFormatter.format(valueCm / 100)} m`;
  }

  renderErrors(errors) {
    Object.entries(FIELD_CONFIG).forEach(([fieldName, config]) => {
      const input = this.refs[config.inputRef];
      const errorElement = this.refs[config.errorRef];
      const message = errors[fieldName] ?? '';

      errorElement.textContent = message;
      errorElement.hidden = message === '';

      if (message) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    });
  }

  renderResults(result) {
    const roundedValue = this.formatMetersFr(result.roundedBiasCm);
    const roundedSuffix = this.refs.roundedValue.nextElementSibling?.textContent?.trim() ?? '';

    this.refs.roundedValue.textContent = roundedValue;
    this.refs.results.hidden = false;
    this.refs.liveRegion.textContent = this.#replaceTokens(this.#messages.announcementTemplate ?? '', {
      value: `${roundedValue} ${roundedSuffix}`.trim(),
    });
  }

  resetCalculator() {
    this.refs.form.reset();
    this.#hasCalculatedOnce = false;
    this.renderErrors({});
    this.#clearResults();
  }

  #calculateAndRender({ focusFirstError }) {
    const rawValues = this.#getRawValues();
    const validation = this.validateInputs(rawValues);

    this.renderErrors(validation.errors);

    if (!validation.isValid) {
      this.#clearResults();

      if (focusFirstError) {
        this.#focusFirstError(validation.errors);
      }

      return;
    }

    const coefficient = this.getSkirtCoefficient(validation.values.skirtType);

    if (coefficient === null) {
      return;
    }

    const hemCm = this.calculateHemCm(validation.values.waistCm, validation.values.lengthCm, coefficient);
    const biasWithAllowanceCm = this.addAllowanceCm(hemCm);
    const roundedBiasCm = this.roundUpToNearestTenCm(biasWithAllowanceCm);

    this.renderResults({
      hemCm,
      biasWithAllowanceCm,
      roundedBiasCm,
    });

    this.#hasCalculatedOnce = true;
  }

  #getRawValues() {
    return {
      waistCm: this.refs.waistInput.value.trim(),
      lengthCm: this.refs.lengthInput.value.trim(),
      skirtType: this.refs.typeSelect.value.trim(),
    };
  }

  #validateNumberField({ rawValue, fieldName, errors }) {
    const config = FIELD_CONFIG[fieldName];
    const input = this.refs[config.inputRef];
    const min = Number(input.min);
    const max = Number(input.max);

    if (rawValue === '') {
      errors[fieldName] = this.#messages.errors?.[config.requiredMessage] ?? '';
      return null;
    }

    const parsedValue = this.#parseNumber(rawValue);

    if (!Number.isFinite(parsedValue)) {
      errors[fieldName] = this.#messages.errors?.[config.invalidMessage] ?? '';
      return null;
    }

    if (parsedValue < min || parsedValue > max) {
      errors[fieldName] = this.#replaceTokens(this.#messages.errors?.range ?? '', {
        min,
        max,
      });
      return null;
    }

    return parsedValue;
  }

  #parseNumber(rawValue) {
    const normalizedValue = rawValue.replace(/\s+/g, '').replace(',', '.');

    if (normalizedValue === '') return NaN;

    return Number(normalizedValue);
  }

  #focusFirstError(errors) {
    if (errors.waist) {
      this.refs.waistInput.focus();
      return;
    }

    if (errors.length) {
      this.refs.lengthInput.focus();
      return;
    }

    if (errors.type) {
      this.refs.typeSelect.focus();
    }
  }

  #clearResults() {
    this.refs.roundedValue.textContent = '';
    this.refs.liveRegion.textContent = '';
    this.refs.results.hidden = true;
  }

  #replaceTokens(template, replacements) {
    return Object.entries(replacements).reduce((result, [key, value]) => {
      return result.replaceAll(`[[${key}]]`, String(value));
    }, template);
  }

  #readMessages() {
    try {
      return JSON.parse(this.refs.messages.textContent ?? '{}');
    } catch (error) {
      console.error('Unable to parse bias calculator messages.', error);
      return {};
    }
  }
}

if (!customElements.get('bias-calculator-component')) {
  customElements.define('bias-calculator-component', BiasCalculator);
}
